import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { mdiOpenInNew, mdiPencilOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { useFolderLayouts, useUpdateContract, refreshContractDocumentFolders } from '../../hooks/useQueries'
import { getLocalizedName } from '../../lib/localization'

/**
 * Sentinel for the "inherit from the contract type" choice — Radix's Select
 * forbids an empty-string item value. Mapped back to `null` on submit, which
 * the API reads as "clear the column" (NullableUUID).
 */
const NONE_VALUE = '__none__'

interface ContractFolderLayoutDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    org: string
    contractUid: string
    /**
     * The layout currently set on this contract's *own* column, not the
     * resolved/inherited one. Undefined → the dialog opens on "inherit".
     */
    currentLayoutUid?: string | null
}

/**
 * Picks the document folder layout of a single contract (spec 2026-08-13-01
 * §6) — the contract twin of `AssetFolderLayoutDialog`.
 *
 * Two differences with the asset side, both structural:
 *   - every contract may carry its own layout (there is no site-level rule and
 *     therefore no subtree warning): the fallback is its contract *type*;
 *   - the picker only lists `target_entity_type = 'contract'` layouts, because
 *     the API answers 422 for anything else.
 */
export function ContractFolderLayoutDialog({
    open,
    onOpenChange,
    org,
    contractUid,
    currentLayoutUid,
}: ContractFolderLayoutDialogProps) {
    const { t, i18n } = useTranslation('folders')
    const queryClient = useQueryClient()
    const [selected, setSelected] = useState<string>(NONE_VALUE)
    const [error, setError] = useState<string | null>(null)

    const { data: layouts, isLoading } = useFolderLayouts(org, open, 'contract')
    const updateContract = useUpdateContract(org, contractUid)

    useEffect(() => {
        if (!open) return
        setSelected(currentLayoutUid || NONE_VALUE)
        setError(null)
    }, [open, currentLayoutUid])

    const handleSave = useCallback(async () => {
        setError(null)
        const layoutUid = selected === NONE_VALUE ? null : selected
        try {
            await updateContract.mutateAsync({ document_folder_layout_uid: layoutUid } as never)
            refreshContractDocumentFolders(queryClient, org, contractUid)
            onOpenChange(false)
        } catch (err: unknown) {
            const payload = err as { fields?: Array<{ name?: string; message?: string }>; title?: string; message?: string }
            const fieldError = payload?.fields?.find(f => f.name === 'document_folder_layout_uid')?.message
            setError(fieldError || payload?.fields?.[0]?.message || payload?.title || payload?.message || t('contract_view.layout_dialog.error'))
        }
    }, [selected, updateContract, queryClient, org, contractUid, onOpenChange, t])

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="contract-folder-layout-dialog">
                <DialogHeader>
                    <DialogTitle>{t('contract_view.layout_dialog.title')}</DialogTitle>
                    <DialogDescription>{t('contract_view.layout_dialog.description')}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="contract-folder-layout-select">
                            {t('contract_view.layout_dialog.select_label')}
                        </Label>
                        <div className="flex gap-2">
                            <Select value={selected} onValueChange={setSelected} disabled={isLoading}>
                                <SelectTrigger id="contract-folder-layout-select" className="flex-1" data-testid="contract-folder-layout-select">
                                    <SelectValue />
                                </SelectTrigger>
                                {/* Capped and scrollable — same treatment as the asset picker. */}
                                <SelectContent className="max-h-72 [&>div]:!h-auto [&>div]:max-h-[17rem] [&>div]:overflow-y-auto">
                                    <SelectItem value={NONE_VALUE} data-testid="contract-folder-layout-option-none">
                                        {t('contract_view.layout_dialog.inherit_option')}
                                    </SelectItem>
                                    {layouts.map(layout => (
                                        <SelectItem
                                            key={layout.uid}
                                            value={layout.uid as string}
                                            data-testid={`contract-folder-layout-option-${layout.code}`}
                                        >
                                            <span>{getLocalizedName(layout.name as Record<string, string>, i18n.language) || layout.code}</span>
                                            <span className="ml-2 text-xs text-muted-foreground">{layout.code}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Button
                                asChild={selected !== NONE_VALUE}
                                variant="outline"
                                size="icon"
                                disabled={selected === NONE_VALUE}
                                aria-label={t('contract_view.layout_dialog.edit_layout')}
                                title={t('contract_view.layout_dialog.edit_layout')}
                                data-testid="contract-folder-layout-edit-btn"
                            >
                                {selected === NONE_VALUE ? (
                                    <AppIcon path={mdiPencilOutline} size="sm" />
                                ) : (
                                    <Link to="/$org/folder-layouts/$layoutUid" params={{ org, layoutUid: selected }}>
                                        <AppIcon path={mdiPencilOutline} size="sm" />
                                    </Link>
                                )}
                            </Button>
                        </div>
                    </div>

                    {error && (
                        <p
                            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                            role="alert"
                            data-testid="contract-folder-layout-error"
                        >
                            {error}
                        </p>
                    )}
                </div>

                <DialogFooter className="sm:justify-between">
                    <Link
                        to="/$org/folder-layouts"
                        params={{ org }}
                        className="inline-flex items-center gap-1 self-center text-xs text-muted-foreground underline-offset-2 hover:underline"
                        data-testid="contract-folder-layout-manage-link"
                    >
                        <AppIcon path={mdiOpenInNew} size="xs" />
                        {t('contract_view.layout_dialog.manage_link')}
                    </Link>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            data-testid="contract-folder-layout-cancel"
                        >
                            {t('contract_view.layout_dialog.cancel')}
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={updateContract.isPending}
                            data-testid="contract-folder-layout-save"
                        >
                            {t('contract_view.layout_dialog.save')}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
